
import { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

interface EditableNotesProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => Promise<boolean> | boolean;
  placeholder?: string;
  isLoading?: boolean;
  contentId: string;
}

const EditableNotes = ({
  value,
  onChange,
  onSave,
  placeholder = "Add notes about this content",
  isLoading = false,
  contentId
}: EditableNotesProps) => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const originalValueRef = useRef(value);
  const isSavingRef = useRef(false);
  
  // Update local value when value prop changes and we're not editing
  useEffect(() => {
    if (!editing && !isSavingRef.current) {
      console.log("[EditableNotes] Received new value:", value);
      setLocalValue(value);
      originalValueRef.current = value;
    }
  }, [value, editing]);

  // Auto-resize textarea when value changes
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && editing) {
      adjustTextareaHeight(textarea);
    }
  }, [localValue, editing]);

  const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = "auto";
    // Set height to scrollHeight to fit content
    textarea.style.height = `${Math.max(textarea.scrollHeight, 40)}px`;
  };

  const handleDoubleClick = () => {
    if (isLoading) return;
    
    console.log("[EditableNotes] Double-clicked, starting edit mode");
    setEditing(true);
    setLocalValue(value); // Reset local value to current value when starting to edit
    originalValueRef.current = value;
    
    // Focus and adjust height after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();
        adjustTextareaHeight(textareaRef.current);
      }
    }, 0);
  };

  const handleChange = (newValue: string) => {
    console.log("[EditableNotes] Value changed");
    setLocalValue(newValue);
    onChange(newValue); // Propagate the change to parent component
    
    // Remove the setTimeout auto-resize that was interfering with text selection
    // The useEffect will handle resizing instead
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only handle the specific keys we care about, let everything else pass through
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveChanges();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
    // Let all other keys (including Cmd+A) pass through naturally
  };

  const saveChanges = async () => {
    if (isLoading) return;
    
    console.log("[EditableNotes] Saving changes. Previous:", originalValueRef.current, "New:", localValue);
    setEditing(false);
    
    // Only save if the value has actually changed
    if (localValue !== originalValueRef.current) {
      isSavingRef.current = true;
      try {
        const result = await onSave();
        
        if (result === false) {
          // If save failed, revert to the original value
          console.log("[EditableNotes] Save failed, reverting to original value:", originalValueRef.current);
          setLocalValue(originalValueRef.current);
          onChange(originalValueRef.current);
        } else {
          // ANALYTICS: Track content editing for notes
          try {
            console.log(`[ANALYTICS DEBUG] Tracking content notes edited: contentId=${contentId}`);
            await Tier1Tracking.trackContentEdited(contentId, ['notes'], user);
            console.log(`[ANALYTICS DEBUG] Successfully tracked content notes editing`);
          } catch (analyticsError) {
            console.error('[ANALYTICS DEBUG] Failed to track content notes editing:', analyticsError);
          }
          
          // Update our reference to the new value
          console.log("[EditableNotes] Save successful, updating reference to:", localValue);
          originalValueRef.current = localValue;
        }
      } catch (error) {
        console.error("[EditableNotes] Error during save:", error);
        // If an error occurred, revert to the original value
        setLocalValue(originalValueRef.current);
        onChange(originalValueRef.current);
      } finally {
        isSavingRef.current = false;
      }
    }
  };

  const cancelEdit = () => {
    console.log("[EditableNotes] Cancelling edit, resetting to:", originalValueRef.current);
    setLocalValue(originalValueRef.current); // Reset to original value
    onChange(originalValueRef.current); // Propagate the reset to parent component
    setEditing(false);
  };

  const handleBlur = async () => {
    // Don't do anything if we're loading
    if (isLoading) return;
    
    // Only save if the value has actually changed
    console.log("[EditableNotes] Textarea blurred. Current:", localValue, "Original:", originalValueRef.current);
    if (localValue !== originalValueRef.current) {
      await saveChanges();
    } else {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <Textarea
        ref={textareaRef}
        id={`notes-textarea-${contentId}`}
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="resize-none overflow-hidden transition-all"
        placeholder={placeholder}
        style={{ minHeight: "40px" }}
        disabled={isLoading}
        data-testid="editable-notes-textarea"
      />
    );
  }
  
  // Display mode
  const isEmpty = !localValue || localValue.trim() === '';
  
  if (isEmpty) {
    return (
      <div 
        className="min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-muted-foreground italic cursor-pointer hover:bg-gray-50 flex items-center"
        onDoubleClick={handleDoubleClick}
        title="Double-click to add notes"
        data-testid="editable-notes-empty"
      >
        {placeholder}
      </div>
    );
  }
  
  return (
    <div 
      className={cn(
        "min-h-[40px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 whitespace-pre-wrap",
        isLoading && "opacity-50"
      )}
      onDoubleClick={handleDoubleClick}
      title="Double-click to edit notes"
      data-testid="editable-notes-display"
    >
      {localValue}
    </div>
  );
};

export default EditableNotes;
